import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  IconButton,
  Alert,
  Snackbar,
  Tooltip,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import CloseIcon from "@mui/icons-material/Close";
import React, { useEffect, useState } from "react";
import CommonGrid from "../../../components/Repository/CommonGrid";
import { useDispatch, useSelector } from "react-redux";
import { api } from "../../../redux/api";
import { treeActions } from "../../../redux/slices/treeSlice";
import { cloneDeep } from "lodash";
import axios from "axios";
import LightTooltip from "../../../commonComponents/LightTooltip";

function CustomIconRenderer(params) {
  return (
    <>
      <div style={{ display: "flex" }}>
        <span className={"REVISION_HISTORY"} style={{ margin: "5px" }}></span>
        <p style={{ margin: "0px 5px" }}>{params.value}</p>
      </div>
    </>
  );
}
const SCREEN_NAME = {
  inboundTemplate: "inbound_templates/",
  INBOUND: "inbound_templates/",
  outboundTemplate: "outbound_templates/",
  SCHEDULE: "schedules/",
  OUTBOUND: "outbound_templates/",
  workflow: "",
  WORKFLOW: "",
};
export const getTemplatePath = (account, workflowGroup, type, title) => {
  return `${account}/${workflowGroup}/${SCREEN_NAME[type]}${title}`;
};
function ServerRevisionHistory({
  open,
  onClose,
  node,
  onRefreshTree,
  urlParams,
}) {
  const { t } = useTranslation();
  const [gridApi, setGridApi] = useState(null);
  const { user, account, baseUrl, token } = useSelector(
    (state) => state.queryParams
  );
  const treeData = useSelector((state) => state.treeData);
  const currentScreen = useSelector((state) => state.treeData.currentScreen);
  const dispatch = useDispatch();
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedAssociatedFiles, setSelectedAssociatedFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [compareModal, setCompareModal] = useState(false);
  const [htmlContent, setHtmlContent] = useState(null);
  const [diffLoading, setDiffLoading] = useState(false);
  const [path, setPath] = useState("");
  const [alertModal, setAlertModal] = useState({
    isOpen: false,
    message: "",
    type: "info",
  });
  const [commentModal, setCommentModal] = useState({
    isOpen: false,
    data: {},
  });
  const [rowData, setRowData] = useState([]);
  const [associatedFilesData, setAssociatedFilesData] = useState([]);
  const [selectedRevisionForAssociatedFiles, setSelectedRevisionForAssociatedFiles] = useState(null);
  const [associatedFilesGridApi, setAssociatedFilesGridApi] = useState(null);

  const gridOptions = {
    columnDefs: [
      {
        headerName: t("REVISION_ID"),
        field: "revision_id",
        showPlaceholder: false,
        resizable: true,
        cellRenderer: CustomIconRenderer,
        flex: 1,
      },
      {
        headerName: t("USER_INTRACT_USERNAME"),
        field: "user",
        showPlaceholder: false,
        resizable: true,
        flex: 1,
      },
      {
        headerName: t("DATE_"),
        field: "date",
        showPlaceholder: false,
        resizable: true,
        flex: 1,
      },
      {
        headerName: t("LBL_NEW_COMMENT"),
        field: "comment",
        showPlaceholder: false,
        resizable: true,
        flex: 1,
      },
      {
        headerName: t("LBL_ASSOCIATED_FILES"),
        field: "associated_files",
        showPlaceholder: false,
        resizable: true,
        flex: 1,
      },
    ],
  };

  const associatedFilesGridOptions = {
    columnDefs: [
      {
        headerName: t("REVISION_ID"),
        field: "revision_id",
        showPlaceholder: false,
        resizable: true,
        cellRenderer: CustomIconRenderer,
        flex: 1,
      },
      {
        headerName: t("USER_INTRACT_USERNAME"),
        field: "user",
        showPlaceholder: false,
        resizable: true,
        flex: 1,
      },
      {
        headerName: t("DATE_"),
        field: "date",
        showPlaceholder: false,
        resizable: true,
        flex: 1,
      },
      {
        headerName: t("MNU_FILE"),
        field: "path",
        showPlaceholder: false,
        resizable: true,
        flex: 2,
      },
    ],
  };

  const onAlertClose = () =>
    setAlertModal({ isOpen: false, message: "", type: "info" });

  const onCommentClose = () => setCommentModal({ isOpen: false, data: {} });

  const compareModalHandler = () => {
    setCompareModal(!compareModal);
    if (!compareModal) {
      setHtmlContent(null);
    }
  };

  const fetchDiffContent = async (firstRev, secondRev) => {
    setDiffLoading(true);
    try {
      const url = `${baseUrl}/workbench?user=${user}&action=get_server_revision_diff&account=${account}&path=${path}&rev_id1=${firstRev.revision_id}&rev_id2=${secondRev.revision_id}`;

      const response = await axios(url, {
        responseType: "text",
        headers: { Authorization: `Bearer ${token}` },
      });

      setHtmlContent(response.data);
    } catch (error) {
      console.error("Error fetching diff:", error);
      setHtmlContent(error.response?.data || "Error fetching diff data");
      setAlertModal({
        isOpen: true,
        message: t("COMPARE_ERROR"),
        type: "error",
      });
    } finally {
      setDiffLoading(false);
    }
  };

  function onSelectionChanged() {
    const rows = gridApi.getSelectedNodes();
    if (rows.length > 0) {
      const rowData = rows.map((row) => {
        return row.data;
      });
      setSelectedRows(rowData);
      
      // Handle associated files display for single selection
      if(rows.length === 1) {
        const selectedRow = rows[0].data;
        if(selectedRow.relatedGroup && Array.isArray(selectedRow.relatedGroup) && selectedRow.relatedGroup.length > 0) {
          // Convert relatedGroup data to the format expected by the associated files grid
          const associatedFiles = selectedRow.relatedGroup.map((file, index) => ({
            id: index,
            revision_id: file.revisionId,
            user: file.lastModifiedUser,
            date: file.lastModifiedDate,
            path: file.path
          }));
          setAssociatedFilesData(associatedFiles);
          setSelectedRevisionForAssociatedFiles(selectedRow);
        } else {
          setAssociatedFilesData([]);
          setSelectedRevisionForAssociatedFiles(null);
        }
      } else {
        setAssociatedFilesData([]);
        setSelectedRevisionForAssociatedFiles(null);
      }
    } else {
      setSelectedRows([]);
      setAssociatedFilesData([]);
      setSelectedRevisionForAssociatedFiles(null);
    }
  }

  function onAssociatedFilesSelectionChanged() {
    const rows = associatedFilesGridApi.getSelectedNodes();
    if (rows.length > 0) {
      const rowData = rows.map((row) => {
        return row.data;
      });
      setSelectedAssociatedFiles(rowData);
    } else {
      setSelectedAssociatedFiles([]);
    }
  }

  const onCellDoubleClicked = (params) => {
    setCommentModal({
      isOpen: true,
      data: params.data,
    });
  };

  const getGridAPI = (api) => {
    setGridApi(api);
  };

  const getAssociatedFilesGridAPI = (api) => {
    setAssociatedFilesGridApi(api);
  };

  const handleRetrieveAction = () => {
    if (selectedRows.length === 0) {
      setAlertModal({
        isOpen: true,
        message: t("BOX_RETRIEVE_ALERT"),
        type: "warning",
      });
      return;
    }

    if (selectedRows.length !== 1) {
      setAlertModal({
        isOpen: true,
        message: "You must select ONE revision from the top revision history table.",
        type: "warning",
      });
      return;
    }

    // Remove file extension from path before sending to API
    const pathWithoutExtension = selectedRows[0].path.replace(/\.(png|jpeg|gif|jpg|pdf|html|xml|json)$/i, '');
    const apiType = {
      "INBOUND": "saveUserInboundTemplate",
      "OUTBOUND": "saveUserOutboundTemplate",
      "WORKFLOW": "saveUserWorkflowTemplate",
    }
    dispatch(
      api.endpoints.getTemplateByServerRevId.initiate({
        account: account,
        path: pathWithoutExtension,
        revId: selectedRows[0].revision_id,
      })
    )
      .then((res) => {
        if (
          res?.data?.data?.data?.TEMPLATE ||
          res?.data?.data?.data?.WORKFLOW_DEFINITION
        ) {
          dispatch(api.endpoints[apiType[node.type]].initiate({
            account: account,
            workflowGroupName: node?.workflowGroup,
            templateName: node?.name || node?.title,
            data: res?.data?.data?.data
          })).then(() => {
              setAlertModal({
                isOpen: true,
                message: t("RETRIEVE_SUCCESS"),
                type: "success",
              });
              // onClose();
          })
          
          // Refresh tree data after successful retrieve
          if (onRefreshTree) {
            onRefreshTree();
            onClose();
          }
        } else {
          setAlertModal({
            isOpen: true,
            message: t("RETRIEVE_FAILED"),
            type: "error",
          });
          onClose();
        }
      })
      .catch((error) => {
        setAlertModal({
          isOpen: true,
          message: t("RETRIEVE_ERROR"),
          type: "error",
        });
        onClose();
      });
  };

  const getApiTypeFromPath = (filePath) => {
    if (filePath.includes('inbound_templates/')) {
      return 'saveUserInboundTemplate';
    } else if (filePath.includes('outbound_templates/')) {
      return 'saveUserOutboundTemplate';
    } else {
      return 'saveUserWorkflowTemplate';
    }
  };

  // Helper function to get template name and workflow group from file path
  const getTemplateDetailsFromPath = (filePath) => {
    const pathParts = filePath.split('/');
    if (pathParts.length >= 3) {
      const workflowGroup = pathParts[1];
      let templateName;
      
      if (filePath.includes('inbound_templates/') || filePath.includes('outbound_templates/')) {
        // For inbound/outbound: account/workflowGroup/template_type/templateName
        templateName = pathParts[3];
      } else {
        // For workflow: account/workflowGroup/templateName
        templateName = pathParts[2];
      }
      
      return { workflowGroup, templateName };
    }
    
    return {
      workflowGroup: node?.workflowGroup,
      templateName: node?.name || node?.title
    };
  };

  const handleRetrieveGroupAction = async () => {
    if (selectedAssociatedFiles.length === 0) {
      setAlertModal({
        isOpen: true,
        message: "You must select one or more files from the Associated File table (Bottom table).",
        type: "warning",
      });
      return;
    }

    try {

      const retrievePromises = selectedAssociatedFiles.map(async (file) => {
        try {
          // Remove file extension from path before sending to API
          const pathWithoutExtension = file.path.replace(/\.(png|jpeg|gif|jpg|pdf|html|xml|json)$/i, '');
          
          const response = await dispatch(
            api.endpoints.getTemplateByServerRevId.initiate({
              account: account,
              path: pathWithoutExtension,
              revId: file.revision_id,
            }, {forceRefetch: true})
          ).unwrap(); 
          
          
          if (response?.data?.data?.TEMPLATE || response?.data?.data?.WORKFLOW_DEFINITION) {
            
            const apiType = getApiTypeFromPath(file.path);
            const { workflowGroup, templateName } = getTemplateDetailsFromPath(file.path);
            
            
            await dispatch(api.endpoints[apiType].initiate({
              account: account,
              workflowGroupName: workflowGroup,
              templateName: templateName,
              data: response?.data?.data
            })).unwrap();
            
            return {
              file: file.path,
              success: true,
              response
            };
          } else {
            return {
              file: file.path,
              success: false,
              response
            };
          }
        } catch (error) {
          console.error(`Error retrieving file ${file.path}:`, error);
          return {
            file: file.path,
            success: false,
            error
          };
        }
      });

      // Wait for all API calls to complete
      const results = await Promise.allSettled(retrievePromises);
      
      // Process results
      let successCount = 0;
      const totalFiles = selectedAssociatedFiles.length;
      
      results.forEach((result, index) => {
        if (result.status === 'fulfilled' && result.value.success) {
          successCount++;
        } else {
          console.error(`Failed to retrieve file ${selectedAssociatedFiles[index].path}:`, 
            result.status === 'rejected' ? result.reason : result.value.error);
        }
      });

      // Show appropriate message based on results
      if (successCount === totalFiles) {
        setAlertModal({
          isOpen: true,
          message: `Successfully retrieved ${successCount} file(s).`,
          type: "success",
        });
      } else if (successCount > 0) {
        setAlertModal({
          isOpen: true,
          message: `Retrieved ${successCount} out of ${totalFiles} file(s). Some files failed to retrieve.`,
          type: "warning",
        });
      } else {
        setAlertModal({
          isOpen: true,
          message: "Failed to retrieve any files.",
          type: "error",
        });
      }

      // Refresh tree data after successful retrieve
      if (onRefreshTree && successCount > 0) {
        onRefreshTree();
      }

      if (successCount > 0) {
        onClose();
      }
    } catch (error) {
      console.error("Error in handleRetrieveGroupAction:", error);
      setAlertModal({
        isOpen: true,
        message: "Error occurred while retrieving files.",
        type: "error",
      });
    }
  };

  const handleCompareAction = () => {
    if (selectedRows?.length === 2) {
      const revisionArray = cloneDeep(selectedRows);
      revisionArray.sort((a, b) => {
        return a.revision_id - b.revision_id;
      });

      fetchDiffContent(revisionArray[0], revisionArray[1]);
      compareModalHandler();
    } else {
      setAlertModal({
        isOpen: true,
        message: t("BOX_SEL_REV"),
        type: "warning",
      });
    }
  };

  useEffect(() => {
    if (open && node && Object.keys(node).length > 0) {
      // Reset associated files data when opening for a different file
      setAssociatedFilesData([]);
      setSelectedRevisionForAssociatedFiles(null);
      setSelectedRows([]);
      setSelectedAssociatedFiles([]);
      
      const templatePath = getTemplatePath(
        account,
        node.workflowGroup,
        node.type,
        node.title
      )
        .split(".")
        .slice(0, -1)
        .join("");
      setPath(templatePath);

      dispatch(
        api.endpoints.getServerRevisionhistory.initiate(
          {
            account,
            path: templatePath,
          },
          { forceRefetch: true }
        )
      )
        .then((response) => {
          const data = response.data.data;
          if (response.data.current_state === "SUCCESS") {
            const updatedRowData = data.map((row, index) => {
              // Process associated files from relatedGroup
              let associatedFilesText = `${row.relatedGroup.length} files uploaded with this file`;
              
              return {
                id: index,
                revision_id: row.revisionId,
                user: row.lastModifiedUser,
                date: row.lastModifiedDate,
                comment: row.comment,
                path: row.path,
                associated_files: associatedFilesText,
                relatedGroup: row.relatedGroup 
              };
            });
            setRowData(updatedRowData);
          }
        })
        .catch((error) => {
          setRowData([]);
          setAlertModal({
            isOpen: true,
            message: t("LOAD_ERROR"),
            type: "error",
          });
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [open, node, account, dispatch, t]);

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        maxWidth={"md"}
        fullWidth>
        <DialogTitle className="dialog-header">
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <div className="title">{t("LBL_SERVER_REV")}</div>
            <IconButton onClick={onClose} sx={{ borderRadius: "5px" }}>
              <CloseIcon
                sx={{ height: "28px", width: "28px", color: "#777777" }}
              />
            </IconButton>
          </div>
        </DialogTitle>
        <DialogContent sx={{ height: "650px", padding: 0}} dividers>
          <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
            {/* Header Description */}
            <div className={"dialog-content"} style={{ 
              padding: "16px", 
              paddingBottom: "8px",
              flexShrink: 0
            }}>
              <span>{t("DIAG_REVISION_HISTORY_DESC")}</span>
            </div>
            
            {/* Main Revision History Grid */}
            <div style={{ 
              height: selectedRevisionForAssociatedFiles && associatedFilesData.length > 0 ? "250px" : "400px",
              padding: "0 16px",
              display: "flex",
              flexDirection: "column"
            }}>
              <CommonGrid
                rowData={rowData}
                gridOptions={gridOptions}
                getGridAPI={getGridAPI}
                isLoading={isLoading}
                gridProps={{
                  animateRows: false,
                  enableCellTextSelection: true,
                  ensureDomOrder: true,
                  rowSelection: "multiple",
                  onSelectionChanged: onSelectionChanged,
                  onCellDoubleClicked: onCellDoubleClicked,
                }}
                fullSize={true}
              />
            </div>

            {/* Buttons after main grid */}
            <div style={{ 
              padding: "8px 16px", 
              display: "flex", 
              justifyContent: "flex-end",
              gap: "8px",
              flexShrink: 0
            }}>
              <Tooltip
                title={
                  selectedRows.length < 2 ? t("SELECT_TWO_ROWS_TO_COMPARE") : ""
                }>
                <span>
                  <Button
                    size="small"
                    variant={"outlined"}
                    onClick={handleCompareAction}
                    disabled={selectedRows.length < 2}>
                    {t("BTN_COMPARE")}
                  </Button>
                </span>
              </Tooltip>
              <Tooltip
                title={
                  selectedRows.length !== 1
                    ? "Select ONE revision from the top revision history table"
                    : ""
                }>
                <span>
                  <Button
                    size="small"
                    variant={"outlined"}
                    onClick={handleRetrieveAction}
                    disabled={selectedRows.length !== 1}>
                    {t("BTN_RETRIEVE")}
                  </Button>
                </span>
              </Tooltip>
            </div>
              
            {/* Associated Files Section */}
            {selectedRevisionForAssociatedFiles && associatedFilesData.length > 0 && (
              <>
                {/* Separator text */}
                <div style={{ 
                  padding: "8px", 
                  fontSize: "12px",
                  borderTop: "1px solid #e0e0e0",
                  flexShrink: 0
                }}>
                  <span>The following files were uploaded at the same time as {selectedRevisionForAssociatedFiles.path}</span>
                </div>
                
                {/* Associated Files Grid */}
                <div style={{ 
                  height: "200px",
                  padding: "8px 16px 0px 16px",
                  display: "flex",
                  flexDirection: "column"
                }}>
                  <CommonGrid
                    rowData={associatedFilesData}
                    gridOptions={associatedFilesGridOptions}
                    getGridAPI={getAssociatedFilesGridAPI}
                    isLoading={false}
                    gridProps={{
                      animateRows: false,
                      enableCellTextSelection: true,
                      ensureDomOrder: true,
                      rowSelection: "multiple",
                      onSelectionChanged: onAssociatedFilesSelectionChanged,
                    }}
                    fullSize={true}
                  />
                </div>

                {/* Buttons after associated files grid */}
                <div style={{ 
                  padding: "8px 16px", 
                  display: "flex", 
                  justifyContent: "flex-end",
                  gap: "8px",
                  flexShrink: 0
                }}>
                  <LightTooltip
                    title={
                      selectedAssociatedFiles.length === 0
                        ? "Select one or more files from the Associated File table"
                        : ""
                    }>
                    <span>
                      <Button
                        size="small"
                        variant={"outlined"}
                        onClick={handleRetrieveGroupAction}
                        disabled={selectedAssociatedFiles.length === 0}>
                        {t("BTN_RETRIEVE")}
                      </Button>
                    </span>
                  </LightTooltip>
                </div>
              </>
            )}
            
              <div className={"diag-lrh-ib_footer"} style={{ 
                padding: "8px 16px", 
                flexShrink: 0,
                marginTop: "auto"
              }}>
                <span>{t("DIAG_COMMENT_HINT")}</span>
              </div>
          </div>
        </DialogContent>

      </Dialog>

      {commentModal.isOpen && (
        <Dialog
          open={commentModal.isOpen}
          maxWidth={"xs"}
          fullWidth
          onClose={onCommentClose}>
          <DialogTitle>
            <div className="diag-lrh-ib">
              {t("REVISION")} {commentModal.data.revision_id}
            </div>
          </DialogTitle>
          <DialogContent dividers>
            <div
              className={"diag-lrh-ib_header"}
              style={{ marginTop: ".5rem", marginBottom: "1rem" }}>
              <span>
                {t("REVISION")}: {commentModal.data.revision_id}
              </span>
              <span>
                {t("USER")}: {commentModal.data.user}
              </span>
              <span>
                {t("DATE_")}: {commentModal.data.date}
              </span>
              <span>
                {t("COMMENT")}: {commentModal.data.comment}
              </span>
            </div>
          </DialogContent>
          <DialogActions>
            <Button size="small" variant={"outlined"} onClick={onCommentClose}>
              {t("BTN_OK")}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {compareModal && (
        <Dialog
          open={compareModal}
          onClose={compareModalHandler}
          aria-labelledby="diff-dialog-title"
          aria-describedby="diff-dialog-description"
          maxWidth={"xl"}
          fullWidth>
          <DialogTitle>
            <div className="compare-local-revision_header dialog-header">
              <h1>{path}</h1>
            </div>
          </DialogTitle>
          <DialogContent
            className="compare-local-revisions_body height-100"
            dividers>
            <div id="local-revision-diff-container" style={{ height: "100%" }}>
              {htmlContent ? (
                <iframe
                  srcDoc={htmlContent}
                  style={{ width: "100%", height: "100%", border: "none" }}
                  title="HTML Content Viewer"></iframe>
              ) : diffLoading ? (
                <div
                  className="loading-overlay"
                  style={{ marginTop: "15%", display: "grid", gap: "20px" }}>
                  <CircularProgress size={"4rem"} />
                  <div>{t("LOADING_IND")}</div>
                </div>
              ) : (
                ""
              )}
            </div>
          </DialogContent>
          <DialogActions>
            <Button
              size="small"
              onClick={compareModalHandler}
              variant={"outlined"}>
              {t("BTN_CLOSE")}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {alertModal.isOpen && (
        <Snackbar
          open={alertModal.isOpen}
          autoHideDuration={6000}
          onClose={onAlertClose}>
          <Alert
            severity={alertModal.type}
            onClose={onAlertClose}
            variant="filled"
            sx={{
              width: "100%",
              maxWidth: "450px",
              whiteSpace: "normal",
              wordBreak: "break-word",
            }}>
            {alertModal.message}
          </Alert>
        </Snackbar>
      )}
    </>
  );
}

export default ServerRevisionHistory;
